import { useCallback } from 'react'
import {setError} from 'slices/issue-notes'
import {FilterMap} from './use-filters-list'

export interface IUseInputChangedCallbackArgs {
    inputRe: RegExp[],
    getLookupFieldReadOnly: Function,
    loadItem: Function,
    handleSelectionChanged: Function,
    globalFilters: FilterMap,
    openLookup: Function,
    selectionMode: 'single' | 'multi' | 'multicsv' | 'range' | 'singlerange',
    setValue: Function,
}

export const useInputChangedCallback = ({
    inputRe,
    getLookupFieldReadOnly,
    loadItem,
    handleSelectionChanged,
    globalFilters,
    openLookup,
    selectionMode,
    setValue
}: IUseInputChangedCallbackArgs) => {

    /*const callbackLocalId = useCallback(async (match: RegExpMatchArray): {success: boolean, lookupValue?: string} => {
        const availableGlobalFilters = config.globalFilterNames
            ? config.globalFilterNames.filter((val) => match.groups?.[val] && !getLookupFieldReadOnly(val))
            : []

        let _globalFilters = {}

        if (availableGlobalFilters.length > 0) {
            // use loadItem fn to check if such item exists in the db
            _globalFilters = availableGlobalFilters.reduce((filters, filterName: string) => ({ ...filters, [filterName.toLowerCase()]: match.groups?.[filterName] }), {})
        } else {
            if (config.globalFilterNames) {

                if (Object.keys(globalFilters).length === config.globalFilterNames.length) {
                    _globalFilters = globalFilters
                } else {
                    // todo: figure another way to initiate search
                    match.groups?.localId && openLookup(match.groups.localId)
                    return { success: false, lookupValue: match.groups?.localId }
                }
            } else {
                _globalFilters = {}
                // match.groups.localId && setNextValue(castToType(match.groups.localId))
            }
        }
        if (match.groups?.localId){
            try{
                const data = await loadItem(_globalFilters, { [config.localFilterName]: match.groups.localId })
                handleSelectionChanged(data)
                return { success: true, lookupValue: undefined }
            } catch (error) {
                // match.groups?.localId && openLookup(match.groups?.localId)
                return { success: false, lookupValue: match.groups?.localId }
            }
        } else {
            return { success: false, lookupValue: undefined }
        }
}, [config, getLookupFieldReadOnly, loadItem, handleSelectionChanged, globalFilters, openLookup])*/

const callbackSingleRe = useCallback(async (singleInputRe: RegExp, value: string): Promise<{success: boolean, lookupValue?: string}> => {
        const match = value.match(singleInputRe)

        if (match && match.groups) {
            const keys = Object.keys(match.groups)
            const lastKey = keys[keys.length - 1]
            let _requestFilters = keys.reduce((filters, filterName: string) => (
                { ...filters, [filterName.toLowerCase()]: match.groups?.[filterName] ? match.groups?.[filterName] : globalFilters[filterName.toLowerCase()] }
            ), {})

            if (Object.values(_requestFilters).some(v => v === undefined)) {
                return { success: false, lookupValue: match.groups[lastKey] }
            }

            try {
                const data = await loadItem(globalFilters, _requestFilters)
                handleSelectionChanged(data)
                return { success: true, lookupValue: undefined }
            } catch (error) {
                return { success: false, lookupValue: match.groups[lastKey] }
            }
        } else {
            return { success: false, lookupValue: value }
        }
    }, [loadItem, handleSelectionChanged, globalFilters])

    const callback = useCallback(async (value: string | null) => {
        if (value === null || value === '') {
            handleSelectionChanged(null)
            return
        }

        let lastlookupValue: string | undefined
        let anySuccess = false

        if (Array.isArray(inputRe)) {
            for (const singleRe of inputRe) {
                const result = await callbackSingleRe(singleRe, value)
                if (result.success) {
                    anySuccess = true
                    break
                } else {
                    lastlookupValue = result.lookupValue ? result.lookupValue : lastlookupValue
                }
            }
        } else {
            const result = await callbackSingleRe(inputRe, value)
            anySuccess = result.success
            lastlookupValue = result.lookupValue
        }

        // If no inputRe succeeded, open lookup with the last lookup value
        if (!anySuccess) {
            openLookup(lastlookupValue)
        }
    }, [inputRe, callbackSingleRe, handleSelectionChanged, openLookup])

    const rangeCallback = useCallback((value: string | null) => {
        if (value === null || value === '') {
            handleSelectionChanged(null)
            return
        }

        const items = value.split(',').map(i => i.trim())

        const closedRanges: string[] = []
        const openRanges: string[] = []
        const multiselectItems: string[] = []

        items.forEach((item: string) => {
            if (item.includes('-')) {
                // handle range items
                if (item.endsWith('-')) {
                    // open range, need to open dropdown to select end of range
                    openRanges.push(item.slice(0, -1))
                } else {
                    closedRanges.push(item)
                }
            } else if (inputRe.some(_inputRe => item.match(_inputRe))) {
                multiselectItems.push(item)
            }
        })

        const val = [...closedRanges, ...multiselectItems].join(',')
        setValue(val || null)

        if (openRanges.length) {
            // todo: open lookup to select range end
            openLookup(undefined, openRanges[0])
        }
    }, [inputRe, handleSelectionChanged, setValue, openLookup])

    return selectionMode === 'range' || selectionMode === 'singlerange' ? rangeCallback : callback
}
